import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import useCurrencyCode from '@alltrails/context/hooks/useCurrencyCode';
import usePlans from '@alltrails/context/hooks/usePlans';
import useUser from '@alltrails/context/hooks/useUser';
import Typography from '@alltrails/core/components/Typography';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import Button from '@alltrails/denali/components/Button';
import Heading from '@alltrails/denali/components/Heading';
import PlusLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Plus';
import welcomeHeaderSrc from './header-image.avif';
import styles from './Header.module.scss';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

const WelcomeHeader = () => {
  const intl = useIntl();
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);

  const currencyCode = useCurrencyCode();
  const plans = usePlans();

  const annualCost = plans.plus.priceTotal;
  const monthlyCost = plans.plus.priceMonth;

  const params = new URLSearchParams(useSearchParams());
  const search = params.toString();
  const checkoutUrl = wrapUrlSafe(`/plans/checkout${search.length > 0 ? `?${search}` : ''}`, languageRegionCode);

  return (
    <div className={styles.container}>
      <div className={styles.descriptionContainer}>
        <div className={styles.innerContainer}>
          <PlusLogoBadge size="md" variant="full" />
          <div className={styles.headerContainer}>
            <Heading as="h1" className={styles.header} size="600">
              <FormattedMessage defaultMessage="The world's best hiking app" />
            </Heading>
            <Typography className={styles.subCopy} variant="paragraph300" component="p">
              <FormattedMessage defaultMessage="Find trails for you and adventure offline with AllTrails Plus." />
            </Typography>
          </div>
          <div className={styles.buttonContainer}>
            <Button
              text={<FormattedMessage defaultMessage="Start your free trial" />}
              {...(user ? { linkInfo: { href: checkoutUrl } } : { onClick: () => setIsSignUpModalOpen(true) })}
              variant="primary"
            />
            <Typography variant="text100">
              <FormattedMessage
                defaultMessage="7 days free, then {annualCost}/year (that’s {monthlyCost}/month)"
                values={{
                  annualCost: <FormattedNumber currency={currencyCode} style="currency" value={annualCost} />,
                  monthlyCost: <FormattedNumber currency={currencyCode} style="currency" value={monthlyCost} />
                }}
              />
            </Typography>
          </div>
        </div>
      </div>
      <div className={styles.imageContainer}>
        <Image src={welcomeHeaderSrc} alt={intl.formatMessage({ defaultMessage: 'Hikers exploring a mountain trail.' })} className={styles.image} />
      </div>
      {isSignUpModalOpen && (
        <SignUpModal
          isOpen
          closeModal={() => setIsSignUpModalOpen(false)}
          onSuccess={() => {
            setIsSignUpModalOpen(false);
            window.location.assign(checkoutUrl);
          }}
          trigger="Upgrade"
          upsellTrigger="Upgrade"
          hideUpsell
        />
      )}
    </div>
  );
};

export default WelcomeHeader;
